import { type CreatedEmployee, CreateEmployeeInput, enrolmentUrl } from "@cleanbudget/contract/employee";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent } from "@cleanbudget/ui/components/card";
import { Field, FieldLabel } from "@cleanbudget/ui/components/field";
import { Input } from "@cleanbudget/ui/components/input";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ButtonLink } from "@/components/button-link";
import { Form, FormError, SubmitButton, TextField, useZodForm } from "@/components/form/form";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

export const Route = createFileRoute("/_admin/settings/employee/new")({
	component: NewEmployeePage,
});

/**
 * Invite an employee: a form on `useZodForm` with the contract's `CreateEmployeeInput`, so the
 * browser checks exactly what the API checks. The API mails the enrolment link; it is shown here
 * too, to pass on by hand when the mail did not go out (outside production it never does).
 */
function NewEmployeePage() {
	const { t } = useTranslation("employee");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const [created, setCreated] = useState<CreatedEmployee>();
	const form = useZodForm(CreateEmployeeInput, { defaultValues: { name: "", email: "" } });
	const create = useMutation({
		...orpc.employee.create.mutationOptions(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.employee.key() }),
	});

	const onSubmit = async (values: CreateEmployeeInput) => {
		try {
			setCreated(await create.mutateAsync(values));
		} catch (error) {
			match(apiErrorTag(error))
				.with("EmployeeEmailTaken", () =>
					form.setError("email", { type: "server", message: t("emailTaken") }),
				)
				.otherwise(() => form.setError("root", { type: "server", message: tCommon("form.failed") }));
		}
	};

	return (
		<div className="flex max-w-lg flex-col gap-6">
			<PageHeader
				back={<BackLink to="/settings/employee">{t("title")}</BackLink>}
				title={t("new.title")}
				description={created ? undefined : t("new.description")}
			/>
			{created ? (
				<CreatedCard
					created={created}
					onAnother={() => {
						setCreated(undefined);
						form.reset();
					}}
				/>
			) : (
				<Card>
					<CardContent>
						<Form form={form} onSubmit={onSubmit}>
							<TextField control={form.control} name="name" label={t("field.name")} autoComplete="off" />
							<TextField
								control={form.control}
								name="email"
								type="email"
								label={t("field.email")}
								description={t("field.emailDescription")}
								autoComplete="off"
							/>
							<FormError control={form.control} />
							<div>
								<SubmitButton control={form.control}>{t("new.submit")}</SubmitButton>
							</div>
						</Form>
					</CardContent>
				</Card>
			)}
		</div>
	);
}

const dateTime = new Intl.DateTimeFormat("nl-NL", {
	dateStyle: "long",
	timeStyle: "short",
	timeZone: "Europe/Amsterdam",
});

function CreatedCard({ created, onAnother }: { created: CreatedEmployee; onAnother: () => void }) {
	const { t } = useTranslation("employee");
	const [copied, setCopied] = useState(false);
	const id = useId();
	const link = enrolmentUrl(window.location.origin, created.enrolment.token);
	const name = created.employee.name;

	const copy = async () => {
		await navigator.clipboard.writeText(link);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Card>
			<CardContent className="flex flex-col gap-4">
				<div className="flex flex-col gap-1">
					<h2 className="font-heading text-base font-medium">{t("created.title", { name })}</h2>
					<p className="text-sm text-muted-foreground">
						{t(`created.${created.invitation.status}`, {
							name,
							email: created.employee.email,
							expiresAt: dateTime.format(new Date(created.enrolment.expiresAt)),
						})}
					</p>
				</div>
				<Field>
					<FieldLabel htmlFor={id}>{t("created.link")}</FieldLabel>
					<div className="flex gap-2">
						<Input id={id} readOnly value={link} onFocus={(event) => event.currentTarget.select()} />
						<Button variant="outline" onClick={copy}>
							{copied ? <IconCheck aria-hidden /> : <IconCopy aria-hidden />}
							{copied ? t("created.copied") : t("created.copy")}
						</Button>
					</div>
				</Field>
				<div className="flex flex-wrap gap-2">
					<ButtonLink to="/settings/employee">{t("created.done")}</ButtonLink>
					<Button variant="outline" onClick={onAnother}>
						{t("created.another")}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}
